// ---------------------------------------------------------------------------
// Date/time helpers. The practice operates in US Central (America/Chicago) (§6).
// Availability dates/times come back as Central; "today" / same-day cutoffs are
// relative to "now" in Central. We compute Central wall-clock via Intl rather
// than pulling in a date library.
// ---------------------------------------------------------------------------

const TZ = 'America/Chicago'

const ymdFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
})

/** "Today" in Central as YYYY-MM-DD. */
export function todayCentral(): string {
  // en-CA formats as YYYY-MM-DD.
  return ymdFmt.format(new Date())
}

function pad(n: number): string {
  return String(n).padStart(2, '0')
}

/** Add days to a YYYY-MM-DD string (calendar arithmetic, tz-agnostic). */
export function addDaysYmd(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + n)
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`
}

/** Day of week for a YYYY-MM-DD (0 = Sunday). */
export function dayOfWeek(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay()
}

export function isSunday(ymd: string): boolean {
  return dayOfWeek(ymd) === 0
}

/** The practice is closed Sundays — roll a Sunday start forward to Monday (§6.1). */
export function nextNonSunday(ymd: string): string {
  return isSunday(ymd) ? addDaysYmd(ymd, 1) : ymd
}

const dowNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const monthNames = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

/** "Thu, Jun 25" */
export function formatDateShort(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return `${dowNames[dt.getUTCDay()]}, ${monthNames[m - 1]} ${d}`
}

/** Weekday + date as separate parts, for two-line calendar column headers. */
export function dateHeaderParts(ymd: string): { dow: string; md: string } {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return { dow: dowNames[dt.getUTCDay()], md: `${monthNames[m - 1]} ${d}` }
}

/** "Thursday, June 25, 2026" */
export function formatDateLong(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return `${days[dt.getUTCDay()]}, ${months[m - 1]} ${d}, ${y}`
}

/** "14:30" → "2:30 PM" (display only; we always send 24h to the API). */
export function formatTime12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${pad(m)} ${period}`
}

/** Short timezone label for "now" in Central (e.g. "CDT" / "CST"). */
export function centralTzAbbrev(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    timeZoneName: 'short'
  }).formatToParts(new Date())
  return parts.find((p) => p.type === 'timeZoneName')?.value || 'CT'
}

const centralHmFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
})

/** "Now" in Central as HH:mm (24h) — used to stamp after-hours visits. */
export function nowCentralHHmm(): string {
  // en-GB 24h formats as "HH:mm"; guard the occasional "24:xx" at midnight.
  return centralHmFmt.format(new Date()).replace(/^24/, '00')
}

/**
 * How many milliseconds a time zone is ahead of UTC at a given instant.
 * Positive east of UTC. Handles DST because it asks Intl for the wall clock.
 */
function tzOffsetMs(timeZone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })
  const p = dtf.formatToParts(at)
  const get = (t: string) => Number(p.find((x) => x.type === t)?.value)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'))
  return asUtc - at.getTime()
}

/**
 * Convert a Central (America/Chicago) wall-clock date + time to a UTC instant,
 * formatted as `YYYYMMDDTHHMMSSZ` for calendar links. DST-correct.
 */
export function centralToUtcStamp(ymd: string, hhmm: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  const [h, min] = hhmm.split(':').map(Number)
  // First approximation: treat the wall time as if it were UTC.
  const guess = Date.UTC(y, m - 1, d, h, min, 0)
  // The real UTC instant is the guess minus Central's offset at that moment.
  const offset = tzOffsetMs(TZ, new Date(guess))
  const instant = new Date(guess - offset)
  return (
    `${instant.getUTCFullYear()}${pad(instant.getUTCMonth() + 1)}${pad(instant.getUTCDate())}` +
    `T${pad(instant.getUTCHours())}${pad(instant.getUTCMinutes())}${pad(instant.getUTCSeconds())}Z`
  )
}

/** Add minutes to an `HH:mm` (same-day; wraps are not expected for slots). */
export function addMinutesToStamp(ymd: string, hhmm: string, minutes: number): { ymd: string; hhmm: string } {
  const [y, m, d] = ymd.split('-').map(Number)
  const [h, min] = hhmm.split(':').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d, h, min))
  dt.setUTCMinutes(dt.getUTCMinutes() + minutes)
  return {
    ymd: `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`,
    hhmm: `${pad(dt.getUTCHours())}:${pad(dt.getUTCMinutes())}`
  }
}
